// Escolha Mensal / Anual (mockup do checkout, tela 1 e 2). Cards são rádios
// de verdade (role="radio"), navegáveis por teclado e com alvo ≥ 44px.
import React from "react";
import moment from "moment";
import { makeStyles, alpha } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import { AEROS } from "../../styles/aerosTokens";
import { formatBRL, mesesEconomizados, round2 } from "./checkoutRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    grid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 10,
      "@media (max-width:820px)": { gridTemplateColumns: "1fr" },
    },
    single: { gridTemplateColumns: "1fr" },
    card: {
      position: "relative",
      textAlign: "left",
      minHeight: 44,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`,
      borderRadius: 12,
      padding: "12px 14px",
      cursor: "pointer",
      color: theme.palette.text.primary,
      fontFamily: "inherit",
      touchAction: "manipulation",
      transition: "border-color 150ms ease-out, box-shadow 150ms ease-out",
      "&:focus-visible": {
        outline: `2px solid ${AEROS.cyan}`,
        outlineOffset: 2,
      },
      "&:disabled": { cursor: "default" },
    },
    on: {
      borderColor: alpha("#A044FF", 0.6),
      boxShadow: `0 0 0 1px ${alpha("#A044FF", 0.25)}`,
    },
    tag: {
      position: "absolute",
      top: -9,
      right: 10,
      fontSize: 9.5,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})`,
      color: "#07080D",
      borderRadius: 6,
      padding: "2px 7px",
    },
    name: { fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 },
    radio: {
      width: 14,
      height: 14,
      borderRadius: "50%",
      border: `2px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.25)"}`,
      flex: "none",
    },
    radioOn: {
      borderColor: theme.palette.primary.main,
      background: `radial-gradient(circle, ${theme.palette.primary.main} 45%, transparent 50%)`,
    },
    price: {
      display: "block",
      fontSize: 20,
      fontWeight: 800,
      letterSpacing: "-0.02em",
      marginTop: 6,
      fontVariantNumeric: "tabular-nums",
      "& small": {
        fontSize: 11,
        fontWeight: 500,
        color: theme.palette.text.secondary,
        letterSpacing: 0,
      },
    },
    hint: { display: "block", fontSize: 11, color: theme.palette.text.secondary, marginTop: 2 },
  };
});

const PeriodCards = ({ prices, period, onChange, periods, cycleStartsToday, disabled }) => {
  const classes = useStyles();
  const saved = mesesEconomizados(prices);
  const day = moment(cycleStartsToday || undefined).format("DD");

  const info = {
    MENSAL: {
      label: i18n.t("subscription.checkout.monthly"),
      suffix: i18n.t("subscription.checkout.perMonth"),
      hint: i18n.t("subscription.checkout.chargedOnDay", { day }),
    },
    ANUAL: {
      label: i18n.t("subscription.checkout.yearly"),
      suffix: i18n.t("subscription.checkout.perYear"),
      hint: i18n.t("subscription.checkout.approxMonthly", {
        value: formatBRL(round2(Number(prices?.ANUAL || 0) / 12)),
      }),
      tag:
        saved > 1
          ? i18n.t("subscription.checkout.saveMonths", { count: saved })
          : saved === 1
          ? i18n.t("subscription.checkout.saveOneMonth")
          : null,
    },
  };

  return (
    <div
      role="radiogroup"
      aria-label={i18n.t("subscription.checkout.period")}
      className={`${classes.grid} ${periods.length === 1 ? classes.single : ""}`}
    >
      {periods.map((p) => {
        const on = p === period;
        return (
          <button
            key={p}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={disabled}
            className={`${classes.card} ${on ? classes.on : ""}`}
            onClick={() => onChange(p)}
          >
            {info[p].tag && <span className={classes.tag}>{info[p].tag}</span>}
            <span className={classes.name}>
              <span className={`${classes.radio} ${on ? classes.radioOn : ""}`} />
              {info[p].label}
            </span>
            <span className={classes.price}>
              {formatBRL(prices?.[p])}
              <small> {info[p].suffix}</small>
            </span>
            <span className={classes.hint}>{info[p].hint}</span>
          </button>
        );
      })}
    </div>
  );
};

export default PeriodCards;
